import { useQuery } from '@tanstack/react-query';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { MetaApp } from '../types/meta.types';
import { getAppImpactoExclusao } from '@/services/api/connections';

interface DeleteAppDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    app: MetaApp | null;
    onConfirm: () => void;
    isLoading: boolean;
}

function Linha({ rotulo, ativos, inativos }: { rotulo: string; ativos: number; inativos?: number }) {
    return (
        <div className="flex items-baseline justify-between text-sm py-1">
            <span className="text-gray-700">{rotulo}</span>
            <span className="font-semibold text-gray-900">
                {ativos + (inativos ?? 0)}
                {inativos !== undefined && (
                    <span className="ml-2 font-normal text-gray-500">
                        {ativos} ativos · {inativos} inativos
                    </span>
                )}
            </span>
        </div>
    );
}

export default function DeleteAppDialog({
                                            open,
                                            onOpenChange,
                                            app,
                                            onConfirm,
                                            isLoading,
                                        }: DeleteAppDialogProps) {
    const { data: impacto, isLoading: carregando, isError } = useQuery({
        queryKey: ['meta-app-impacto-exclusao', app?.id],
        queryFn: () => getAppImpactoExclusao(app!.id),
        enabled: open && !!app,
        staleTime: 0,
    });

    if (!app) return null;

    const definitivo = impacto?.definitivo ?? !app.is_active;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[480px]">
                <DialogHeader>
                    <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                        <AlertTriangle className="h-6 w-6 text-red-600" />
                    </div>
                    <DialogTitle className="text-2xl font-bold text-gray-900 text-center">
                        {definitivo ? 'Excluir app definitivamente?' : 'Desativar app?'}
                    </DialogTitle>
                    <DialogDescription className="text-gray-600 text-center">
                        {definitivo
                            ? 'Esta ação não pode ser desfeita. O app, os perfis conectados por ele e os tokens das páginas neste app são apagados. As páginas, os funis e os templates continuam.'
                            : 'O app sai da lista de ativos e para de ser usado no envio. Dá para reativar depois na aba de inativos.'}
                    </DialogDescription>
                </DialogHeader>

                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="text-sm">
                        <span className="font-semibold text-gray-900">App:</span>{' '}
                        <span className="text-gray-700">{app.app_name}</span>
                    </div>
                    <div className="text-sm mt-1">
                        <span className="font-semibold text-gray-900">App ID:</span>{' '}
                        <span className="text-gray-700 font-mono">{app.app_id}</span>
                    </div>
                </div>

                <div className="rounded-lg border border-gray-200 p-4">
                    {carregando && (
                        <div className="flex items-center justify-center text-sm text-gray-500">
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Calculando o impacto...
                        </div>
                    )}
                    {isError && (
                        <p className="text-sm text-red-600">Não foi possível calcular o impacto da exclusão.</p>
                    )}
                    {impacto && (
                        <>
                            <p className="text-sm font-semibold text-gray-900 mb-1">Esse app tem:</p>
                            <Linha rotulo="Perfis conectados" ativos={impacto.perfis_ativos} inativos={impacto.perfis_inativos} />
                            <Linha rotulo="Páginas" ativos={impacto.paginas_ativas} inativos={impacto.paginas_inativas} />
                            <Linha rotulo="Tokens de página" ativos={impacto.tokens} />
                            <Linha rotulo="Páginas travadas neste app" ativos={impacto.paginas_travadas} />
                            {impacto.paginas_travadas > 0 && (
                                <p className="mt-1 text-xs text-gray-500">
                                    Páginas que o Grupo de App fixou para enviar sempre por este app.
                                    {definitivo
                                        ? ' A trava é apagada e elas voltam a escolher app sozinhas.'
                                        : ' Com o app desativado, o envio ignora a trava e usa outro app da página, se houver.'}
                                </p>
                            )}
                            {impacto.paginas_sem_outro_app > 0 && (
                                <p className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-2">
                                    {impacto.paginas_sem_outro_app} página(s) ativa(s) só têm token neste app e
                                    ficam sem caminho de envio.
                                </p>
                            )}
                            {definitivo && impacto.tokens > 0 && (
                                <p className="mt-3 text-sm text-gray-600">Você perde os tokens deste app.</p>
                            )}
                        </>
                    )}
                </div>

                <DialogFooter className="gap-2 sm:gap-0">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isLoading}
                        className="border-gray-300"
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={onConfirm}
                        disabled={isLoading || carregando}
                        className="bg-red-600 hover:bg-red-700 text-white"
                    >
                        {isLoading ? (
                            <>
                                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                Excluindo...
                            </>
                        ) : definitivo ? (
                            'Excluir definitivamente'
                        ) : (
                            'Desativar app'
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
